/**
 * @voltxampere/ui — CanvasRenderer (Sprint 12)
 *
 * Per Blueprint §12.5. Wires together the immutable scene signal,
 * the dirty-region tracker, and the draw helpers. The class owns
 * a tiny rAF loop: any signal write that flips the scene reference
 * triggers a `scheduleFrame()`, which coalesces multiple writes
 * into one `requestAnimationFrame` callback.
 *
 * Two design notes worth flagging:
 *
 * 1. The scheduler is injectable. Production uses `rafScheduler`
 *    (the global `requestAnimationFrame`); unit tests inject a
 *    synchronous scheduler so they can step the loop deterministically
 *    without happy-dom needing rAF.
 * 2. The renderer never reads from the canvas DOM beyond `width /
 *    height` (and only via the `CanvasSizeSink` interface). Real
 *    `<vxa-canvas>` passes the live element; tests pass a plain
 *    object literal.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { effect } from "@preact/signals-core";
import { drawDotGrid } from "./draw/grid.js";
import { drawWire } from "./draw/wire.js";
import { drawComponent } from "./draw/component.js";
import { drawProbeOverlay } from "./draw/probe-overlay.js";
export const rafScheduler = {
    schedule(cb) {
        if (typeof requestAnimationFrame !== "undefined") {
            return requestAnimationFrame(cb);
        }
        return setTimeout(() => {
            cb(performance.now());
        }, 16);
    },
    cancel(id) {
        if (typeof cancelAnimationFrame !== "undefined") {
            cancelAnimationFrame(id);
        }
        else {
            clearTimeout(id);
        }
    },
};
export const FRAME_BUDGET_MS = 20;
export class CanvasRenderer {
    ctx;
    sceneSignal;
    dirty;
    canvas;
    dpr = 1;
    rafId = null;
    disposeEffect;
    scheduler;
    constructor(ctx, sceneSignal, dirty, canvas, opts = {}) {
        this.ctx = ctx;
        this.sceneSignal = sceneSignal;
        this.dirty = dirty;
        this.canvas = canvas;
        this.scheduler = opts.scheduler ?? rafScheduler;
    }
    /**
     * Subscribe to the scene signal and schedule a frame on every
     * mutation. Returns a disposer the caller invokes on teardown.
     */
    start() {
        this.disposeEffect = effect(() => {
            // Read once per effect run — Preact records the dependency.
            void this.sceneSignal.value;
            this.scheduleFrame();
        });
        return () => {
            this.dispose();
        };
    }
    dispose() {
        if (this.rafId !== null) {
            this.scheduler.cancel(this.rafId);
            this.rafId = null;
        }
        this.disposeEffect?.();
        this.disposeEffect = undefined;
    }
    /** Update the device-pixel ratio and trigger a redraw. */
    setDPR(dpr) {
        if (!Number.isFinite(dpr) || dpr <= 0)
            return;
        this.dpr = dpr;
        this.dirty.markAllDirty();
        this.scheduleFrame();
    }
    /** Force a full redraw on the next frame. */
    invalidate() {
        this.dirty.markAllDirty();
        this.scheduleFrame();
    }
    /**
     * Synchronously execute one frame. Used by the rAF callback in
     * production; tests call this directly to step the loop.
     *
     * Sprint 13.0.1: the frame-budget warning measures the *actual*
     * draw duration via `performance.now()`. The previous
     * implementation diffed the rAF timestamp against the prior
     * frame's timestamp, which reports inter-frame *interval* — a
     * 37-second idle then a single redraw incorrectly logged "Frame
     * took 37000ms" while the draw itself ran in microseconds.
     */
    tick(time) {
        this.rafId = null;
        void time;
        const start = performance.now();
        const { full, regions } = this.dirty.consume();
        const scene = this.sceneSignal.value;
        if (full) {
            this.drawFull(scene);
        }
        else if (regions.length > 0) {
            for (const r of regions) {
                this.drawRegion(scene, r);
            }
        }
        const drawMs = performance.now() - start;
        if (drawMs > FRAME_BUDGET_MS) {
            console.warn(`[VXA renderer] Frame draw took ${drawMs.toFixed(1)}ms`);
        }
    }
    // ── internal ─────────────────────────────────────────────────────
    scheduleFrame() {
        if (this.rafId !== null)
            return;
        this.rafId = this.scheduler.schedule((t) => {
            this.tick(t);
        });
    }
    /**
     * CSS-pixel canvas size. `canvas.width / height` are *backing*
     * store dimensions (`cssSize × dpr`); after the renderer applies
     * `setTransform(dpr, 0, 0, dpr, 0, 0)` everything else in the
     * frame must be expressed in CSS pixels so the dpr scale lines up
     * with what the user clicks via `viewport.screenToWorld` (which
     * also reads `getBoundingClientRect()` — CSS pixels).
     *
     * Sprint 13.0.1 root cause: pre-fix the renderer translated by
     * `canvas.width / 2`, which on a Retina (dpr=2) display offset
     * the world origin by an extra `cssWidth/2` pixels, pushing every
     * component down-right of where the pointer hit-test thought it
     * was.
     */
    cssSize() {
        return { w: this.canvas.width / this.dpr, h: this.canvas.height / this.dpr };
    }
    drawFull(scene) {
        const { w: cssW, h: cssH } = this.cssSize();
        this.ctx.save();
        this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        this.ctx.clearRect(0, 0, cssW, cssH);
        this.ctx.fillStyle = scene.theme.bg.void;
        this.ctx.fillRect(0, 0, cssW, cssH);
        // Move into world space: centre + zoom around canvas centre.
        const z = scene.viewport.zoom;
        this.ctx.translate(cssW / 2, cssH / 2);
        this.ctx.scale(z, z);
        this.ctx.translate(-scene.viewport.center.x, -scene.viewport.center.y);
        const layers = [...scene.layers].sort((a, b) => a.zIndex - b.zIndex);
        for (const layer of layers) {
            if (!layer.visible)
                continue;
            this.drawLayer(scene, layer);
        }
        this.ctx.restore();
    }
    drawRegion(scene, rect) {
        const { w: cssW, h: cssH } = this.cssSize();
        this.ctx.save();
        this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        this.ctx.beginPath();
        this.ctx.rect(rect.x, rect.y, rect.w, rect.h);
        this.ctx.clip();
        this.ctx.clearRect(rect.x, rect.y, rect.w, rect.h);
        this.ctx.fillStyle = scene.theme.bg.void;
        this.ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
        const z = scene.viewport.zoom;
        this.ctx.translate(cssW / 2, cssH / 2);
        this.ctx.scale(z, z);
        this.ctx.translate(-scene.viewport.center.x, -scene.viewport.center.y);
        const layers = [...scene.layers].sort((a, b) => a.zIndex - b.zIndex);
        for (const layer of layers) {
            if (!layer.visible)
                continue;
            this.drawLayer(scene, layer);
        }
        this.ctx.restore();
    }
    drawLayer(scene, layer) {
        for (const node of layer.nodes) {
            this.drawNode(scene, node);
        }
    }
    drawNode(scene, node) {
        switch (node.kind) {
            case "grid": {
                // Visible world rect spans the whole canvas size / zoom.
                // Canvas dimensions are backing-store pixels — divide by
                // dpr so the visible rect is in CSS pixels (matches the
                // world-space transform applied in drawFull / drawRegion).
                const z = scene.viewport.zoom;
                const { w: cssW, h: cssH } = this.cssSize();
                const halfW = cssW / (2 * z);
                const halfH = cssH / (2 * z);
                const visible = {
                    x: scene.viewport.center.x - halfW,
                    y: scene.viewport.center.y - halfH,
                    w: halfW * 2,
                    h: halfH * 2,
                };
                drawDotGrid(this.ctx, scene.viewport, visible, scene.theme, node.spacing);
                return;
            }
            case "wire": {
                drawWire(this.ctx, node.wire, scene.theme, node.selected, scene.wireCurve);
                return;
            }
            case "component": {
                drawComponent(this.ctx, { instance: node.instance, selected: node.selected, hover: node.hover }, scene.theme);
                return;
            }
            case "probe-overlay": {
                // Sprint 18 D1 — direct dispatch. Sprint 14 deferred to
                // an "apps/web dispatcher" that never landed; centralising
                // here keeps draw helpers next to their scene-node
                // authority and removes the no-op trapdoor. The text
                // helper accepts `Path2D`-less contexts; CanvasContext2DLike
                // already declares fillText.
                drawProbeOverlay(this.ctx, node.position, scene.theme, node.label, node.intensity);
                return;
            }
            // Sprint 13+ surface — declared but rendered as no-op for now.
            case "selection-box":
            case "ghost":
            case "assertion-marker":
            case "annotation":
            case "net-label":
            case "wire-overlay":
            case "ghost-wire":
            case "erase-preview":
            case "annotation-edit":
                return;
        }
    }
}
//# sourceMappingURL=renderer.js.map